import React, { useEffect, useMemo, useState } from 'react';
import { getUser } from '@/utils/auth';
import { memberApi } from '../api';
import { listLocalCheckins } from '../localStore';
import {
  IMPACT_OPTIONS,
  QUICK_SEVERITIES,
  SYMPTOM_PRESETS,
  appendLog,
  clinicBriefText,
  impactLabel,
  isSymptomKind,
  lastLoggedLabel,
  loggedToday,
  overviewStats,
  parseSchedule,
  recentLogs,
  scheduleLabel,
  severityLabel,
  splitPrompts,
  trendSummary,
} from '../checkins';
import {
  browserTimezone,
  disablePushNotifications,
  enablePushNotifications,
  getPushPermission,
  pushSupported,
} from '../push';
import { CHECKIN_EXAMPLES } from '../prompts';
import { IconBell } from '../components/Icons';

function TrendBars({ points }) {
  if (!points?.length) {
    return <div className="by-trend-empty">Log a few entries to see a short trend.</div>;
  }
  return (
    <div className="by-trend-bars" aria-hidden="true">
      {points.map((value, index) => (
        <span
          key={`${index}-${value}`}
          className="by-trend-bar"
          style={{ height: `${Math.max(8, (value / 10) * 100)}%` }}
          title={`${value}/10`}
        />
      ))}
    </div>
  );
}

function OverviewStrip({ stats, onCopyClinic, copyNote }) {
  if (!stats.trackers) return null;
  return (
    <div className="by-symptom-overview" role="region" aria-label="Symptom overview">
      <div className="by-overview-grid">
        <div>
          <strong>{stats.trackers}</strong>
          <span>Trackers</span>
        </div>
        <div>
          <strong>{stats.logsThisWeek}</strong>
          <span>Logs this week</span>
        </div>
        <div>
          <strong>{stats.streakDays}</strong>
          <span>Day streak</span>
        </div>
        <div>
          <strong>{stats.avgSeverityThisWeek != null ? `${stats.avgSeverityThisWeek}` : '-'}</strong>
          <span>Week avg /10</span>
        </div>
      </div>
      {stats.needsAttention.length ? (
        <p className="by-overview-alert" role="status">
          Recent high severity ({stats.needsAttention.map((row) => `${row.prompt} ${row.severity}/10`).join(' · ')}).
          Please contact your care team or emergency services for severe or worsening symptoms.
        </p>
      ) : null}
      {stats.rising.length ? (
        <p className="by-fine by-overview-rising">
          Trending higher: {stats.rising.slice(0, 4).join(', ')}
        </p>
      ) : null}
      <div className="by-row by-overview-actions">
        <button type="button" className="by-btn by-btn-ghost" onClick={onCopyClinic}>
          Copy clinic visit note
        </button>
        {copyNote ? <span className="by-fine by-push-note">{copyNote}</span> : null}
      </div>
    </div>
  );
}

function SymptomCard({ item, acting, onLog, onRemove, onToggleNotify }) {
  const [severity, setSeverity] = useState(3);
  const [impact, setImpact] = useState(null);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const trend = useMemo(() => trendSummary(item, 14), [item]);
  const history = recentLogs(item, 8).slice().reverse();
  const busy = acting === item.id;
  const notifyOn = item.notify !== false;

  const submit = async () => {
    const payload = { severity, note, recorded_at: new Date().toISOString() };
    if (impact != null) payload.impact = impact;
    await onLog(item, payload);
    setNote('');
    setImpact(null);
    setOpen(false);
  };

  const high = severity >= 7;

  return (
    <div className={`by-list-card by-symptom-card${loggedToday(item.last_completed_at) ? ' is-on' : ''}`}>
      <div className="by-symptom-head">
        <div>
          <h3>{item.prompt}</h3>
          <p className="by-fine" style={{ textAlign: 'left', margin: 0 }}>
            Symptom · {scheduleLabel(item)}
            {item.last_completed_at ? ` · ${lastLoggedLabel(item.last_completed_at)}` : ''}
            {trend.avg != null ? ` · recent avg ${trend.avg}/10` : ''}
          </p>
        </div>
        <span className={`by-trend-pill is-${trend.direction}`}>{trend.label}</span>
      </div>

      <TrendBars points={trend.points.slice(-14)} />

      {open ? (
        <div className="by-severity-panel">
          <div className="by-severity-row">
            <label htmlFor={`sev-${item.id}`}>
              Severity <strong>{severity}</strong> · {severityLabel(severity)}
            </label>
            <div className="by-severity-chips" role="group" aria-label="Quick severity">
              {QUICK_SEVERITIES.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`by-sev-chip${severity === value ? ' is-on' : ''}${value >= 7 ? ' is-high' : ''}`}
                  disabled={busy}
                  onClick={() => setSeverity(value)}
                >
                  {value}
                </button>
              ))}
            </div>
            <input
              id={`sev-${item.id}`}
              className="by-severity-slider"
              type="range"
              min={0}
              max={10}
              step={1}
              value={severity}
              onChange={(e) => setSeverity(Number(e.target.value))}
              disabled={busy}
            />
            <div className="by-severity-scale" aria-hidden="true">
              <span>0 none</span>
              <span>5</span>
              <span>10 severe</span>
            </div>
          </div>
          {high ? (
            <p className="by-severity-flag" role="status">
              Severity {severity} is high. Please contact your care team or emergency services immediately.
            </p>
          ) : null}
          <div className="by-impact-row">
            <span className="by-impact-label">Did this interfere with daily activities?</span>
            <div className="by-impact-chips" role="group" aria-label="Interference">
              {IMPACT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`by-chip by-impact-chip${impact === opt.value ? ' is-on' : ''}`}
                  disabled={busy}
                  onClick={() => setImpact(impact === opt.value ? null : opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            className="by-severity-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note, what helped, what changed, triggers…"
            disabled={busy}
          />
          <div className="by-row">
            <button type="button" className="by-btn by-btn-dark" disabled={busy} onClick={submit}>
              {busy ? 'Saving…' : 'Save log'}
            </button>
            <button type="button" className="by-btn by-btn-ghost" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="by-row by-symptom-actions">
          <button type="button" className="by-btn by-btn-dark" disabled={busy} onClick={() => setOpen(true)}>
            Log severity
          </button>
          <button
            type="button"
            className="by-btn by-btn-ghost"
            disabled={busy}
            onClick={() => onToggleNotify(item, !notifyOn)}
            title={notifyOn ? 'Turn off reminders for this tracker' : 'Turn on reminders for this tracker'}
          >
            {notifyOn ? 'Reminders on' : 'Reminders off'}
          </button>
          <button
            type="button"
            className="by-btn by-btn-danger"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Remove “${item.prompt}”? Past logs for this tracker will be deleted.`)) {
                onRemove(item);
              }
            }}
          >
            Remove
          </button>
        </div>
      )}

      {history.length ? (
        <ul className="by-log-history">
          {history.map((row) => (
            <li key={`${row.recorded_at}-${row.severity}-${row.note}-${row.impact}`}>
              <span className="by-log-sev">
                {row.severity}/10 · {severityLabel(row.severity)}
                {row.impact != null ? ` · ${impactLabel(row.impact)}` : ''}
              </span>
              <span className="by-log-when">
                {new Date(row.recorded_at).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {row.note ? <span className="by-log-note">{row.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ReminderCard({ item, acting, onDone, onRemove, onToggleNotify }) {
  const done = loggedToday(item.last_completed_at);
  const notifyOn = item.notify !== false;
  return (
    <div className={`by-list-card${done ? ' is-on' : ''}`}>
      <h3>{item.prompt}</h3>
      <p className="by-fine" style={{ textAlign: 'left' }}>
        Reminder · {scheduleLabel(item)}
        {item.last_completed_at ? ` · ${lastLoggedLabel(item.last_completed_at)}` : ''}
      </p>
      <div className="by-row">
        <button
          type="button"
          className="by-btn by-btn-ghost"
          disabled={acting === item.id || done}
          onClick={() => onDone(item)}
        >
          {done ? 'Logged today' : 'Mark done'}
        </button>
        <button
          type="button"
          className="by-btn by-btn-ghost"
          disabled={acting === item.id}
          onClick={() => onToggleNotify(item, !notifyOn)}
        >
          {notifyOn ? 'Reminders on' : 'Reminders off'}
        </button>
        <button
          type="button"
          className="by-btn by-btn-danger"
          disabled={acting === item.id}
          onClick={() => onRemove(item)}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

export default function CheckInsPage() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('symptom');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNote, setPushNote] = useState('');
  const [copyNote, setCopyNote] = useState('');
  const [pushState, setPushState] = useState({
    supported: pushSupported(),
    configured: false,
    subscribed: false,
    permission: 'default',
  });

  const stats = useMemo(() => overviewStats(items), [items]);

  const load = async () => {
    try {
      const data = await memberApi.checkins();
      setItems(data.checkins || []);
    } catch {
      setItems(listLocalCheckins(getUser()));
    }
  };

  const refreshPush = async () => {
    if (!pushSupported()) {
      setPushState({ supported: false, configured: false, subscribed: false, permission: 'unsupported' });
      return;
    }
    try {
      const [status, permission] = await Promise.all([
        memberApi.pushStatus().catch(() => ({ configured: false, subscribed: false })),
        getPushPermission(),
      ]);
      setPushState({
        supported: true,
        configured: !!status.configured,
        subscribed: !!status.subscribed,
        permission,
      });
    } catch {
      setPushState((prev) => ({ ...prev, supported: true }));
    }
  };

  useEffect(() => {
    load();
    refreshPush();
  }, []);

  const add = async (prompt, kind = mode) => {
    const lines = splitPrompts(prompt || draft);
    if (!lines.length || busy) return;
    setBusy(true);
    setError('');
    try {
      const created = [];
      const timezone = browserTimezone();
      for (const line of lines) {
        const schedule = parseSchedule(line);
        const isReminder = kind === 'reminder';
        // Presets without a clock still get a gentle daily nudge at 9:00 local.
        const local_hour = schedule.local_hour ?? (isReminder ? null : 9);
        const local_minute = schedule.local_minute ?? (local_hour == null ? null : 0);
        created.push(await memberApi.addCheckin({
          prompt: line,
          cadence: schedule.cadence,
          time_label: schedule.time_label || (local_hour === 9 ? '9 am' : ''),
          local_hour,
          local_minute,
          timezone,
          kind: isReminder ? 'reminder' : 'symptom',
          notify: true,
        }));
      }
      setItems((prev) => [...created.reverse(), ...prev.filter((row) => !created.some((c) => c.id === row.id))]);
      setDraft('');
    } catch (err) {
      setError(err?.message || 'Could not save that tracker. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushNote('');
    setError('');
    try {
      if (pushState.subscribed) {
        await disablePushNotifications();
        setPushNote('Reminders turned off on this device.');
      } else {
        await enablePushNotifications();
        setPushNote('Reminders on. We’ll nudge you when a check-in is due.');
        try {
          await memberApi.pushTest();
        } catch {
          /* optional test may fail if no keys yet, ignore */
        }
      }
      await refreshPush();
    } catch (err) {
      setError(err?.message || 'Could not update notification settings.');
    } finally {
      setPushBusy(false);
    }
  };

  const logSymptom = async (item, entry) => {
    if (acting) return;
    setActing(item.id);
    setError('');
    setItems((prev) => prev.map((row) => (row.id === item.id ? appendLog(row, entry) : row)));
    try {
      const updated = await memberApi.patchCheckin(item.id, { log: entry });
      if (updated?.id) {
        setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, ...updated } : row)));
      }
    } catch (err) {
      setError(err?.message || 'Could not save that log. Try again.');
      await load();
    } finally {
      setActing('');
    }
  };

  const markDone = async (item) => {
    if (acting || loggedToday(item.last_completed_at)) return;
    const stamp = new Date().toISOString();
    setActing(item.id);
    setError('');
    setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, last_completed_at: stamp } : row)));
    try {
      await memberApi.patchCheckin(item.id, { last_completed_at: stamp });
    } catch (err) {
      setError(err?.message || 'Could not mark that done. Try again.');
      await load();
    } finally {
      setActing('');
    }
  };

  const toggleNotify = async (item, notify) => {
    if (acting) return;
    setActing(item.id);
    setError('');
    setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, notify } : row)));
    try {
      const updated = await memberApi.patchCheckin(item.id, {
        notify,
        timezone: item.timezone || browserTimezone(),
        local_hour: item.local_hour,
        local_minute: item.local_minute,
        cadence: item.cadence,
        time_label: item.time_label,
      });
      if (updated?.id) {
        setItems((prev) => prev.map((row) => (row.id === item.id ? { ...row, ...updated } : row)));
      }
    } catch (err) {
      setError(err?.message || 'Could not update reminders for that tracker.');
      await load();
    } finally {
      setActing('');
    }
  };

  const remove = async (item) => {
    if (acting) return;
    setActing(item.id);
    setError('');
    setItems((prev) => prev.filter((row) => row.id !== item.id));
    try {
      await memberApi.deleteCheckin(item.id);
    } catch (err) {
      setError(err?.message || 'Could not remove that item. Try again.');
      await load();
    } finally {
      setActing('');
    }
  };

  const copyClinicNote = async () => {
    setCopyNote('');
    let text = clinicBriefText(items, 14);
    try {
      const remote = await memberApi.checkinClinicBrief(14);
      if (remote?.text) text = remote.text;
    } catch {
      /* use local brief */
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyNote('Copied, paste into a message for your clinic visit.');
    } catch {
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'byonco-symptom-clinic-note.txt';
      a.click();
      URL.revokeObjectURL(url);
      setCopyNote('Downloaded as a text file for your visit.');
    }
  };

  const symptoms = items.filter(isSymptomKind);
  const reminders = items.filter((row) => !isSymptomKind(row));

  return (
    <div className="by-page">
      <div className="by-center">
        <h1>Symptoms</h1>
        <p className="by-lede">
          Track oncology-relevant symptoms on a 0-10 scale, note how much they interfere with daily life,
          and take a clinic-ready summary to your next visit. Reminders nudge you when a check-in is due.
        </p>

        <OverviewStrip stats={stats} onCopyClinic={copyClinicNote} copyNote={copyNote} />

        {pushState.supported ? (
          <div className="by-push-card" role="region" aria-label="Push reminders">
            <div>
              <p className="by-push-title">
                {pushState.subscribed ? 'Reminders are on' : 'Get symptom reminders'}
              </p>
              <p className="by-fine" style={{ textAlign: 'left', margin: '4px 0 0' }}>
                {pushState.configured
                  ? 'Browser notifications when a daily or scheduled check-in is due. Works best with ByOnco installed to your home screen on iPhone.'
                  : 'Server push keys are not configured yet. You can still track symptoms; reminders will unlock once VAPID keys are set.'}
              </p>
              {pushNote ? <p className="by-fine by-push-note">{pushNote}</p> : null}
            </div>
            <button
              type="button"
              className={`by-btn ${pushState.subscribed ? 'by-btn-ghost' : 'by-btn-dark'}`}
              disabled={pushBusy || !pushState.configured}
              onClick={togglePush}
            >
              {pushBusy ? 'Working…' : pushState.subscribed ? 'Turn off' : 'Enable reminders'}
            </button>
          </div>
        ) : (
          <div className="by-push-card">
            <p className="by-fine" style={{ textAlign: 'left', margin: 0 }}>
              This browser does not support push notifications. Try Chrome or Edge on desktop/Android, or install ByOnco on iOS.
            </p>
          </div>
        )}

        <div className="by-symptom-presets" role="group" aria-label="Common symptoms">
          {SYMPTOM_PRESETS.map((name) => {
            const exists = items.some(
              (row) => isSymptomKind(row) && String(row.prompt).toLowerCase() === name.toLowerCase()
            );
            return (
              <button
                key={name}
                type="button"
                className={`by-chip${exists ? ' is-on' : ''}`}
                disabled={busy || exists}
                onClick={() => add(name, 'symptom')}
                title={exists ? 'Already tracking' : `Track ${name}`}
              >
                {name}
              </button>
            );
          })}
        </div>

        <div className="by-mode-toggle" role="tablist" aria-label="Add type">
          <button
            type="button"
            className={mode === 'symptom' ? 'is-on' : ''}
            onClick={() => setMode('symptom')}
          >
            Symptom
          </button>
          <button
            type="button"
            className={mode === 'reminder' ? 'is-on' : ''}
            onClick={() => setMode('reminder')}
          >
            Reminder
          </button>
        </div>

        <form
          className="by-checkin-bar"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <input
            className="by-checkin-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={mode === 'reminder' ? 'Custom reminder prompt…' : 'Custom symptom name…'}
            disabled={busy}
            autoComplete="off"
          />
          <button type="submit" className="by-btn by-btn-dark" disabled={busy || !draft.trim()}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </form>
        {error ? <p className="by-fine" style={{ color: '#8a1f1f', marginTop: -12, marginBottom: 20 }}>{error}</p> : null}

        {symptoms.length || reminders.length ? (
          <div className="by-list">
            {symptoms.map((item) => (
              <SymptomCard
                key={item.id}
                item={item}
                acting={acting}
                onLog={logSymptom}
                onRemove={remove}
                onToggleNotify={toggleNotify}
              />
            ))}
            {reminders.length ? (
              <>
                <h2 className="by-section-label">Reminders</h2>
                {reminders.map((item) => (
                  <ReminderCard
                    key={item.id}
                    item={item}
                    acting={acting}
                    onDone={markDone}
                    onRemove={remove}
                    onToggleNotify={toggleNotify}
                  />
                ))}
              </>
            ) : null}
          </div>
        ) : (
          <div className="by-empty">
            <div className="glyph"><IconBell /></div>
            <h2>Start tracking</h2>
            <p>Pick a common symptom above, or write your own. Severity logs stay private to your account.</p>
          </div>
        )}

        <div className="by-examples" style={{ marginTop: 22, width: '100%' }}>
          {CHECKIN_EXAMPLES.map((line) => (
            <button key={line} type="button" disabled={busy} onClick={() => add(line, 'reminder')}>
              {line}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
